import { useEffect, useMemo, useState } from 'react';
import ItemPropertyTable, {
  ItemProperty,
  useDivider,
  useProperty,
  usePropertyLink,
} from './item-property-table';
import { Item, ItemInternalPricing } from './item-types';
import { ItemAPI } from '../../api/item';
import { dataTransformers } from '../shared/data-transformers';

type ItemInternalInfoProps = { item: Item };

function useItemInternalPricing(item: Item) {
  const [itemPrice, setItemPrice] = useState<ItemInternalPricing | undefined>();
  useEffect(() => {
    if (!item || !item.item_id) return;
    const getItemPrice = async () => {
      const pricingData = await ItemAPI.getProductPricing([
        { item_id: item.item_id },
      ]);
      const pricingDataJson = pricingData.data as ItemInternalPricing[];
      if (pricingDataJson && pricingDataJson[0] != undefined) {
        setItemPrice(pricingDataJson[0]);
      }
    };
    getItemPrice();
  }, [item]);
  return itemPrice;
}

export function ItemInternalPriceInfo({ item }: ItemInternalInfoProps) {
  const itemPrice = useItemInternalPricing(item);
  const priceSpecs = useMemo(() => {
    if (!itemPrice) return [];
    return [
      useProperty('Price Page', itemPrice.price_page_desc),
      useDivider(),
      useProperty(
        `<${itemPrice.price_break}`,
        itemPrice.base_price,
        dataTransformers.currency,
      ),
      useProperty(
        `>=${itemPrice.price_break}`,
        itemPrice.case_price,
        dataTransformers.currency,
      ),
    ];
  }, [itemPrice]);
  return (
    <ItemPropertyTable
      properties={priceSpecs}
      style={{ gridTemplateColumns: '1fr 3fr', padding: '4px' }}
    />
  );
}

export function ItemInternalSupplierInfo({ item }: ItemInternalInfoProps) {
  if (!item || !item.supplier_name) return null;
  const supplierSpecs = useMemo(() => {
    return [
      usePropertyLink(
        'Supplier',
        item.supplier_name,
        `/vendor-supplier-details?vendorId=${item.vendor_id}&supplierId=${item.supplier_id}`,
      ),
      useProperty('Part #', item.supplier_part_no),
      !!item.supplier_item_id
        ? useProperty('Alternative Part #', item.supplier_item_id)
        : undefined,
      !!item.supplier_item_description
        ? useProperty('Supplier Description', item.supplier_item_description)
        : undefined,
      useProperty('Buyer', `${item.buyer_name} (${item.buyer_id})`),
    ].filter((x) => x);
  }, [item]);
  return (
    <ItemPropertyTable
      properties={supplierSpecs}
      style={{
        gridTemplateColumns: 'min-content 1fr',
        padding: '4px',
      }}
    />
  );
}
